// src/components/admin/ChaptersManagement.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';

const ChaptersManagement = () => {
  const [chapters, setChapters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [currentItem, setCurrentItem] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCountry, setFilterCountry] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [stats, setStats] = useState({ total: 0, active: 0, total_members: 0, countries: 0 });
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);

  const [formData, setFormData] = useState({
    chapter_name: '',
    country: '',
    country_code: '',
    chapter_logo: '',
    leader_name: '',
    leader_email: '',
    leader_phone: '',
    member_count: 0,
    status: 'Active',
    established_date: '',
    description: '',
    activities_summary: '',
    website_url: '',
    display_order: 0
  });

  const statusOptions = ['Active', 'Inactive', 'Pending', 'Suspended'];

  useEffect(() => {
    fetchData();
    fetchStats();
  }, [searchTerm, filterCountry, filterStatus]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('adminToken');

      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      let url = `${API_BASE}/admin/chapters?`;
      if (searchTerm) url += `search=${encodeURIComponent(searchTerm)}&`;
      if (filterCountry) url += `country=${encodeURIComponent(filterCountry)}&`;
      if (filterStatus) url += `status=${filterStatus}&`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });

      const data = await response.json();
      if (data.success) {
        setChapters(data.data.chapters || []);
      } else {
        toast.error(data.message || 'Failed to fetch chapters');
      }
    } catch (err) {
      console.error('Error:', err);
      toast.error('Failed to fetch chapters');
    } finally {
      if (loading) setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/chapters/stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setStats(data.data);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const handleCreate = () => {
    setModalMode('create');
    setCurrentItem(null);
    setFormData({
      chapter_name: '', country: '', country_code: '', chapter_logo: '',
      leader_name: '', leader_email: '', leader_phone: '', member_count: 0,
      status: 'Active', established_date: '', description: '',
      activities_summary: '', website_url: '', display_order: 0
    });
    setImagePreview(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setModalMode('edit');
    setCurrentItem(item);
    setFormData({
      chapter_name: item.chapter_name || '',
      country: item.country || '',
      country_code: item.country_code || '',
      chapter_logo: item.chapter_logo || '',
      leader_name: item.leader_name || '',
      leader_email: item.leader_email || '',
      leader_phone: item.leader_phone || '',
      member_count: item.member_count || 0,
      status: item.status || 'Active',
      established_date: item.established_date ? item.established_date.substring(0, 10) : '',
      description: item.description || '',
      activities_summary: item.activities_summary || '',
      website_url: item.website_url || '',
      display_order: item.display_order || 0
    });
    // Set image preview if logo exists
    if (item.chapter_logo) {
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      setImagePreview(`${API_BASE}${item.chapter_logo}?v=${Date.now()}`);
    } else {
      setImagePreview(null);
    }
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this chapter?')) return;

    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin/chapters/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const data = await response.json();
      if (data.success) {
        fetchData();
        fetchStats();
        toast.success('Chapter deleted successfully');
      } else {
        toast.error(data.message || 'Failed to delete chapter');
      }
    } catch (err) {
      toast.error('Failed to delete chapter');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const url = modalMode === 'create'
        ? `${API_BASE}/admin/chapters`
        : `${API_BASE}/admin/chapters/${currentItem.id}`;

      const response = await fetch(url, {
        method: modalMode === 'create' ? 'POST' : 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      if (data.success) {
        setShowModal(false);
        fetchData();
        fetchStats();
        toast.success(`Chapter ${modalMode === 'create' ? 'created' : 'updated'} successfully`);
      } else {
        toast.error(data.message || 'Operation failed');
      }
    } catch (err) {
      toast.error('Operation failed');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, or WebP)');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image size must be less than 5MB');
      return;
    }

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append('logo', file);

      const token = localStorage.getItem('adminToken');
      const API_BASE = process.env.REACT_APP_API_URL;
      if (!API_BASE) {
        throw new Error('API URL not configured');
      }
      const response = await fetch(`${API_BASE}/admin-upload/chapters/logo`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData
      });

      const data = await response.json();
      if (data.success) {
        setFormData(prev => ({ ...prev, chapter_logo: data.filePath }));
        setImagePreview(`${API_BASE}${data.filePath}?v=${Date.now()}`);
        toast.success('Logo uploaded successfully!');
      } else {
        toast.error(data.message || 'Failed to upload logo');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload logo');
    } finally {
      setUploading(false);
    }
  };

  if (loading && chapters.length === 0) {
    return (
      <div className="d-flex justify-content-center align-items-center py-5">
        <div className="spinner-border" style={{ color: '#6B8E23' }} role="status"></div>
        <span className="ms-3 text-muted">Loading...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="h3 fw-bold mb-1" style={{ color: '#1f2937' }}>Chapters Management</h2>
          <p className="text-muted mb-0">Manage ARCA chapters across Africa</p>
        </div>
        <button onClick={handleCreate} className="btn fw-bold shadow-sm" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
          <i className="bi bi-plus-circle me-2"></i>Add Chapter
        </button>
      </div>

      {/* Stats */}
      <div className="row g-3 mb-4">
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #6B8E23' }}>
            <div className="card-body">
              <small className="text-muted">Total Chapters</small>
              <h4 className="fw-bold mb-0" style={{ color: '#6B8E23' }}>{stats.total || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #4CAF50' }}>
            <div className="card-body">
              <small className="text-muted">Active Chapters</small>
              <h4 className="fw-bold mb-0" style={{ color: '#4CAF50' }}>{stats.active || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #2196F3' }}>
            <div className="card-body">
              <small className="text-muted">Total Members</small>
              <h4 className="fw-bold mb-0" style={{ color: '#2196F3' }}>{stats.total_members || 0}</h4>
            </div>
          </div>
        </div>
        <div className="col-md-3">
          <div className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #FF9800' }}>
            <div className="card-body">
              <small className="text-muted">Countries</small>
              <h4 className="fw-bold mb-0" style={{ color: '#FF9800' }}>{stats.countries || 0}</h4>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-4">
              <input type="text" className="form-control" placeholder="Search..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </div>
            <div className="col-md-4">
              <input type="text" className="form-control" placeholder="Filter by country..." value={filterCountry} onChange={(e) => setFilterCountry(e.target.value)} />
            </div>
            <div className="col-md-4">
              <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="">All Status</option>
                {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card border-0 shadow-sm">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead style={{ backgroundColor: '#f8f9fa' }}>
              <tr>
                <th className="px-4 py-3">Chapter</th>
                <th className="py-3">Country</th>
                <th className="py-3">Leader</th>
                <th className="py-3">Members</th>
                <th className="py-3">Status</th>
                <th className="py-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {chapters.length === 0 ? (
                <tr><td colSpan="6" className="text-center py-5 text-muted">No chapters found</td></tr>
              ) : (
                chapters.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3">
                      <div className="d-flex align-items-center">
                        {item.chapter_logo ? (
                          <img
                            src={item.chapter_logo.startsWith('/uploads/') ? `${process.env.REACT_APP_API_URL || ''}${item.chapter_logo}?v=${Date.now()}` : item.chapter_logo}
                            alt={item.chapter_name}
                            className="me-2"
                            style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        ) : (
                          <div className="bg-light rounded me-2" style={{ width: '40px', height: '40px' }}></div>
                        )}
                        <span className="fw-medium">{item.chapter_name}</span>
                      </div>
                    </td>
                    <td className="py-3">
                      {item.country_code && <span className={`fi fi-${item.country_code.toLowerCase()} me-2`}></span>}
                      {item.country}
                    </td>
                    <td className="py-3">{item.leader_name || '—'}</td>
                    <td className="py-3"><span className="badge bg-info">{item.member_count}</span></td>
                    <td className="py-3">
                      <span className={`badge ${item.status === 'Active' ? 'bg-success' : item.status === 'Pending' ? 'bg-warning' : 'bg-secondary'}`}>
                        {item.status}
                      </span>
                    </td>
                    <td className="py-3 text-center">
                      <div className="btn-group btn-group-sm" role="group">
                        <button
                          onClick={() => handleEdit(item)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#2196F3', color: 'white', border: 'none' }}
                        >
                          <i className="bi bi-pencil"></i> Edit
                        </button>
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="btn btn-sm"
                          style={{ backgroundColor: '#dc3545', color: 'white', border: 'none', marginLeft: '4px' }}
                        >
                          <i className="bi bi-trash"></i> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#6B8E23', color: 'white' }}>
                <h5 className="modal-title">{modalMode === 'create' ? 'Add Chapter' : 'Edit Chapter'}</h5>
                <button type="button" className="btn-close btn-close-white" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleSubmit}>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Chapter Name <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="chapter_name" value={formData.chapter_name} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-8">
                      <label className="form-label">Country <span className="text-danger">*</span></label>
                      <input type="text" className="form-control" name="country" value={formData.country} onChange={handleInputChange} required />
                    </div>
                    <div className="col-md-4">
                      <label className="form-label">Country Code</label>
                      <input type="text" className="form-control" name="country_code" value={formData.country_code} onChange={handleInputChange} placeholder="e.g., gh" maxLength="5" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Leader Name</label>
                      <input type="text" className="form-control" name="leader_name" value={formData.leader_name} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Leader Email</label>
                      <input type="email" className="form-control" name="leader_email" value={formData.leader_email} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Leader Phone</label>
                      <input type="text" className="form-control" name="leader_phone" value={formData.leader_phone} onChange={handleInputChange} />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Member Count</label>
                      <input type="number" className="form-control" name="member_count" value={formData.member_count} onChange={handleInputChange} min="0" />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Status</label>
                      <select className="form-select" name="status" value={formData.status} onChange={handleInputChange}>
                        {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Established Date</label>
                      <input type="date" className="form-control" name="established_date" value={formData.established_date} onChange={handleInputChange} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Chapter Logo</label>
                      <input
                        type="file"
                        className="form-control"
                        accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                        onChange={handleImageUpload}
                        disabled={uploading}
                      />
                      <small className="text-muted">Accepted formats: JPEG, PNG, GIF, WebP (Max: 5MB)</small>
                      {uploading && <div className="mt-2"><small className="text-info">Uploading...</small></div>}
                      {imagePreview && (
                        <div className="mt-3">
                          <img src={imagePreview} alt="Preview" style={{ maxWidth: '200px', maxHeight: '200px', objectFit: 'contain' }} className="border rounded" />
                        </div>
                      )}
                    </div>
                    <div className="col-12">
                      <label className="form-label">Website URL</label>
                      <input type="url" className="form-control" name="website_url" value={formData.website_url} onChange={handleInputChange} />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Description</label>
                      <textarea className="form-control" name="description" value={formData.description} onChange={handleInputChange} rows="2"></textarea>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Activities Summary</label>
                      <textarea className="form-control" name="activities_summary" value={formData.activities_summary} onChange={handleInputChange} rows="2"></textarea>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Display Order</label>
                      <input type="number" className="form-control" name="display_order" value={formData.display_order} onChange={handleInputChange} min="0" />
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn" style={{ backgroundColor: '#6B8E23', color: 'white' }} onClick={handleSubmit}>
                  {modalMode === 'create' ? 'Create' : 'Update'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChaptersManagement;
